import { observable, action, computed, flow } from 'mobx';
import { computedFn } from 'mobx-utils';

import { User, AvailableRoles, RoleType } from '@modules/core/models';
import { msal } from '@config/msalConfig';
import { alertStore } from './AlertStore';
import { apiStore } from './ApiStore';
import { HttpClient, RoleUtils } from '@modules/core/utils';

export default class UserStore {
    @observable.ref currentUser: User = { id: '', role: '' };
    @observable.ref roles: RoleType[] = [];

    constructor() {
        this.loadCurrentUser = this.loadCurrentUser.bind(this);
        this.joinOrganization = this.joinOrganization.bind(this);
    }
    // TODO: refactor this store to use RolesApi
    loadCurrentUser = flow(function* (this: UserStore, isInvite = false) {
        try {
            const currentUser = yield HttpClient.TCMS.GET('users/me', {
                fields: 'organization,roles'
            });
            this.currentUser = { ...currentUser, isInvite, isMsalData: false };
        } catch (error) {
            const user = yield msal.instance.getActiveAccount();
            const idTokenClaims = user?.idTokenClaims as {
                [key: string]: any;
            };
            const isDev = process.env.REACT_APP_DEV;

            this.currentUser = {
                id: isDev ? 'oid' : idTokenClaims?.oid || idTokenClaims?.sid,
                firstName: isDev ? 'Admin' : user?.name,
                lastName: '',
                role: '',
                isMsalData: true
            };
        }
    });

    @action loadUserRoles = async () => {
        try {
            let roles: RoleType[];
            if (process.env.REACT_APP_DEV) {
                roles = ['Administrator' as RoleType];
            } else {
                const azureUser = await msal.instance.getActiveAccount();
                const idTokenClaims: { [key: string]: any } = azureUser?.idTokenClaims;
                roles = idTokenClaims?.roles || [];
            }

            this.roles = roles.map((role: string) => AvailableRoles[role]);
            apiStore.setApiByRole();
        } catch (error) {
            alertStore.error(error, () => ({ message: 'Failed to load user`s permissions' }));
        }
    };

    joinOrganization = flow(function* (this: UserStore, organizationId: string) {
        try {
            yield HttpClient.TCMS.PUT('admin/users/me/organization', organizationId);
            yield this.loadCurrentUser();
            alertStore.success({ message: 'You has successfully joined organization' });
        } catch (error) {
            alertStore.error(error, () => ({ message: 'Failed to join organization' }));
        }
    });

    isAccessable = computedFn((accessRoles: RoleType[], exact: boolean) =>
        RoleUtils.isAccessible(accessRoles, exact, this.roles)
    );

    @computed get isSuperUser() {
        return this.isAccessable([RoleType.Superuser, RoleType.Support], false);
    }

    @computed get isSupportUser() {
        return this.isAccessable([RoleType.Support], false) && !this.isAccessable([RoleType.Superuser], false);
    }

    @computed get isUser() {
        return this.isAccessable([RoleType.User], true);
    }

    @computed get isUserWithAccount() {
        return this.isAccessable([RoleType.User, RoleType.Admin], false);
    }
}
